import React, { useState, useEffect } from "react";
import {
  getAllAssetCategories,
  createAssetCategory,
  updateAssetCategory,
  exportAssetCategoriesToExcel,
} from "../../services/CatagoryServices";
import { FiFolder } from "react-icons/fi";
import {
  FaRegEdit,
  FaClipboardList,
  FaPlusCircle,
  FaTrash,
} from "react-icons/fa";
import Select from "react-select";
import { IoMdAdd } from "react-icons/io";
import { CustomTooltip } from "../../components/CustomTooltip";
import { MultiActionDropdown } from "../../components";
import { MdAddchart } from "react-icons/md";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import { Breadcrumb } from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  InputGroup,
  FormControl,
  Badge,
  Spinner,
  Alert,
  Dropdown,
} from "react-bootstrap";
import Pagination from "../Constant/Pagination";
import { FiDownload } from "react-icons/fi";

const CategoryManagement = () => {
  // State for categories
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null); // null means no filter
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    name: "",
    is_active: true,
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryErrors, setCategoryErrors] = useState({});
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryCount, setCategoryCount] = useState(0);
  const categoryPageSize = 10;

  // Bulk form states
  const [bulkCategoryFormData, setBulkCategoryFormData] = useState([
    { name: "", is_active: true },
  ]);
  const [bulkUploadErrors, setBulkUploadErrors] = useState({});
  const statusOptions = [
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ];

  // Fetch all categories without pagination
  const fetchAllCategories = async () => {
    try {
      const response = await getAllAssetCategories({ page_size: 1000 });
      setAllCategories(response.data.results || []);
    } catch (error) {
      console.error("Failed to fetch all categories:", error);
    }
  };

  // Fetch paginated categories
  const fetchCategories = async () => {
    setCategoryLoading(true);
    try {
      const params = {
        page: categoryPage,
        page_size: categoryPageSize,
      };
      if (categorySearch) params.search = categorySearch;
      if (statusFilter !== null) params.is_active = statusFilter;
      const response = await getAllAssetCategories(params);
      setCategories(response.data.results || []);
      setCategoryCount(response.data.count || 0);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setCategoryLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchAllCategories();
  }, [categorySearch, categoryPage, statusFilter]);

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await updateAssetCategory(editingCategory.id, categoryFormData);
        toast.success("Category updated successfully");
      } else {
        await createAssetCategory(categoryFormData);
        toast.success("Category created successfully");
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryFormData({ name: "", is_active: true });
      fetchCategories();
      fetchAllCategories();
    } catch (error) {
      if (error.response?.data) {
        setCategoryErrors(error.response.data);
      }
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      is_active: category.is_active,
    });
    setShowCategoryModal(true);
  };

  const handleCategoryChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCategoryFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setCategoryErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // Bulk category handlers
  const handleBulkCategoryChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const updatedBulkData = [...bulkCategoryFormData];
    updatedBulkData[index] = {
      ...updatedBulkData[index],
      [name]: type === "checkbox" ? checked : value,
    };
    setBulkCategoryFormData(updatedBulkData);
  };

  const addBulkCategoryRow = () => {
    setBulkCategoryFormData([
      ...bulkCategoryFormData,
      { name: "", is_active: true },
    ]);
  };

  const removeBulkCategoryRow = (index) => {
    const updatedBulkData = bulkCategoryFormData.filter((_, i) => i !== index);
    setBulkCategoryFormData(updatedBulkData);
  };

  const handleBulkCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const validData = bulkCategoryFormData.filter(
        (item) => item.name.trim() !== ""
      );
      if (validData.length === 0) {
        setBulkUploadErrors({
          non_field_errors: ["Please add at least one valid category"],
        });
        return;
      }

      await createAssetCategory(validData);
      setShowBulkCategoryModal(false);
      setBulkCategoryFormData([{ name: "", is_active: true }]);
      fetchCategories();
      fetchAllCategories();
    } catch (error) {
      if (error.response?.data) {
        setBulkUploadErrors(error.response.data);
      }
    }
  };

  const handleExportCategories = async () => {
    try {
      const response = await exportAssetCategoriesToExcel({
        search: categorySearch,
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      const fileName =
        response.headers["content-disposition"]
          ?.split("filename=")[1]
          ?.replace(/["]/g, "") || "export.xlsx";

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Excel export failed:", error);
    }
  };

  const StatusBadge = ({ isActive }) => (
    <span
      className={`badge badge-sm ${
        isActive ? "text-bg-success-soft" : "text-bg-danger-soft"
      }`}
    >
      {isActive ? "Active" : "Inactive"}
    </span>
  );

  const categoryColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Subcategories",
      dataIndex: "subcategory_count",
      key: "subcategories",
      render: (count) => (
        <span className="badge text-bg-success-soft rounded-pill">
          {count || 0}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "status",
      render: (isActive) => <StatusBadge isActive={isActive} />,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <CustomTooltip title="Edit " arrow placement="top">
          <Button
            variant="inline-primary"
            size="sm"
            onClick={() => handleEditCategory(record)}
          >
            <FaRegEdit />
          </Button>
        </CustomTooltip>
      ),
    },
  ];

  const renderTable = (
    data,
    columns,
    loading,
    count,
    page,
    pageSize,
    onPageChange
  ) => {
    return (
      <>
        <div className="table-responsive">
          <table className="table fs-6 mb-0 align-middle">
            <thead className="table-light custom-thead">
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={idx}>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render
                        ? col.render(row[col.dataIndex], row)
                        : row[col.dataIndex]}
                    </td>
                  ))}
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    {loading ? "Loading..." : "No data found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Area */}
        <div className="d-flex justify-content-between align-items-center px-3 my-3">
          {/* Page Info will float Left via CSS */}
          <div className="flex-grow-1">
            <Pagination
              currentPage={page}
              recordsPage={pageSize}
              dataLength={count}
              handlePagination={(selectedObj) =>
                onPageChange(selectedObj.selected + 1)
              }
              showPageInfo={true}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <div className="card p-2 mb-2">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-1">
            <div className="flex-grow-1 flex-md-grow-0">
              <h5 className="mb-0 d-flex align-items-center gap-2">
                <FiFolder size={18} className="text-primary" /> {/* Icon */}
                Asset Categories
              </h5>
            </div>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <DebounceSmallSearchInput
              placeholder="Search categories"
              handleSearchChange={(value) => setCategorySearch(value)}
            />

            <GlobalSearchableDropdown
              options={statusOptions}
              selected={
                statusFilter !== null
                  ? statusOptions.find(
                      (option) => option.value === statusFilter
                    )
                  : null
              }
              onSelect={(selectedItem) => {
                setStatusFilter(selectedItem ? selectedItem.value : null);
              }}
              placeholder="Status"
            />

            <MultiActionDropdown
              variant="outline-primary"
              btnIcon={<MdAddchart />}
              btnText="Add"
              btnOnClick={() => {
                setEditingCategory(null);
                setCategoryFormData({ name: "", is_active: true });
                setShowCategoryModal(true);
              }}
              menuOptions={[
                {
                  label: "Single Category",
                  icon: <FaPlusCircle />,
                  onClick: () => {
                    setEditingCategory(null);
                    setCategoryFormData({ name: "", is_active: true });
                    setShowCategoryModal(true);
                  },
                },
                {
                  label: "Bulk Categories",
                  icon: <FaClipboardList />,
                  onClick: () => {
                    setBulkCategoryFormData([{ name: "", is_active: true }]);
                    setBulkUploadErrors({});
                    setShowBulkCategoryModal(true);
                  },
                },
                {
                  label: "Export to Excel",
                  icon: <FiDownload />,
                  onClick: handleExportCategories,
                },
              ]}
            />
            </div>
          </div>
        </div>

      <Card className="gap-1">
        <Card.Body className="p-0">
          {categoryLoading ? (
            <div className="text-center p-3">
              <Spinner animation="border" />
            </div>
          ) : (
            renderTable(
              categories,
              categoryColumns,
              categoryLoading,
              categoryCount,
              categoryPage,
              categoryPageSize,
              setCategoryPage
            )
          )}
        </Card.Body>

        {/* Single Category Modal */}
        <Modal
          show={showCategoryModal}
          onHide={() => setShowCategoryModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editingCategory ? "Edit Category" : "Add New Category"}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleCategorySubmit}>
            <Modal.Body>
              {categoryErrors.non_field_errors && (
                <Alert variant="danger">
                  {categoryErrors.non_field_errors.map((err, idx) => (
                    <div key={idx}>{err}</div>
                  ))}
                </Alert>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Category Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder="Enter category name"
                  value={categoryFormData.name}
                  onChange={handleCategoryChange}
                  isInvalid={!!categoryErrors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {categoryErrors.name}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="category-active"
                  label="Active"
                  name="is_active"
                  checked={categoryFormData.is_active}
                  onChange={handleCategoryChange}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowCategoryModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingCategory ? "Update" : "Create"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Bulk Category Modal */}
        <Modal
          show={showBulkCategoryModal}
          onHide={() => setShowBulkCategoryModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Bulk Add Categories</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleBulkCategorySubmit}>
            <Modal.Body>
              {bulkUploadErrors.non_field_errors && (
                <Alert variant="danger">
                  {bulkUploadErrors.non_field_errors.map((err, idx) => (
                    <div key={idx}>{err}</div>
                  ))}
                </Alert>
              )}

              <div className="mb-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addBulkCategoryRow}
                  className="mb-2"
                >
                  <IoMdAdd /> Add Row
                </Button>

                <Table bordered>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkCategoryFormData.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <Form.Control
                            type="text"
                            name="name"
                            value={item.name}
                            onChange={(e) => handleBulkCategoryChange(index, e)}
                            placeholder="Category name"
                            isInvalid={!!bulkUploadErrors[index]?.name}
                          />
                          {bulkUploadErrors[index]?.name && (
                            <Form.Control.Feedback type="invalid">
                              {bulkUploadErrors[index].name}
                            </Form.Control.Feedback>
                          )}
                        </td>
                        <td>
                          <Form.Check
                            type="switch"
                            id={`category-active-${index}`}
                            name="is_active"
                            checked={item.is_active}
                            onChange={(e) => handleBulkCategoryChange(index, e)}
                            label={item.is_active ? "Active" : "Inactive"}
                          />
                        </td>
                        <td>
                          {bulkCategoryFormData.length > 1 && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeBulkCategoryRow(index)}
                            >
                              <FaTrash />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowBulkCategoryModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Create All
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Card>
    </>
  );
};

export default CategoryManagement;
